import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BlockAbstractComponent, pickContrastingTextColor, SettingsService } from '@gallaghercp/core-frontend';
import { resolveBrandBackgroundHex } from '../../utils/brand-color.util';

@Component({
    selector: 'core-block-home-skinny-cta-banner',
    templateUrl: 'block-home-skinny-cta-banner.component.html',
    standalone: false,
})
export class BlockHomeSkinnyCtaBannerComponent extends BlockAbstractComponent implements OnInit {
    foregroundColorHex: string = '#ffffff';
    linkColorHex?: string;
    private primaryColor?: string;
    private secondaryColor?: string;

    private readonly settings = inject(SettingsService);
    private readonly destroyRef = inject(DestroyRef);

    isLoggedIn: boolean = false;
    hideForLoggedInUsers: boolean = false;

    containerPaddingBlock =
        (this.configService.get('blocks') as any)?.homeSkinnyCtaBanner?.container?.paddingBlock ?? '1rem';
    containerPaddingInline =
        (this.configService.get('blocks') as any)?.homeSkinnyCtaBanner?.container?.paddingInline ?? '0';

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.push(
            this.auth.authChangeEmit.subscribe(() => {
                this.isLoggedIn = this.auth.isAuthenticated();
            }),
        );

        // In demo/configurable environments, these can be overridden at runtime.
        this.settings
            .getPrimaryColor()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(color => {
                this.primaryColor = color;
                this.refreshContrast();
                this.refreshLinkColor();
            });

        this.settings
            .getSecondaryColor()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(color => {
                this.secondaryColor = color;
                this.refreshContrast();
                this.refreshLinkColor();
            });

        this.refreshContrast();
        this.refreshLinkColor();
    }

    hideForLoggedInUsersChange() {
        this.hideForLoggedInUsers = (this.block?.data as any)?.hideForLoggedInUsers ?? false;
        return this.hideForLoggedInUsers && this.isLoggedIn;
    }

    getBackgroundClass(): string {
        switch (this.getBackgroundSelection()) {
            case 'Tertiary Colour':
            case 'Tertiary Color':
            case 'tertiary':
                return 'home-skinny-cta--tertiary';
            case 'Secondary Colour':
            case 'Secondary Color':
            case 'secondary':
                return 'home-skinny-cta--secondary';
            case 'Primary Colour':
            case 'Primary Color':
            case 'primary':
            default:
                return 'home-skinny-cta--primary';
        }
    }

    getBannerSizeClass(): string {
        switch (this.getBannerSizeSelection()) {
            case 'Small':
                return 'home-skinny-cta--size-small';
            case 'Medium':
                return 'home-skinny-cta--size-medium';
            case 'Large':
            default:
                return 'home-skinny-cta--size-large';
        }
    }

    getBannerTextAlignmentClass(): string {
        switch (this.getBannerTextAlignmentSelection()) {
            case 'Center':
                return 'home-skinny-cta--text-align-center';
            case 'Right':
                return 'home-skinny-cta--text-align-right';
            case 'Left':
            default:
                return 'home-skinny-cta--text-align-left';
        }
    }

    private getBackgroundSelection(): string | undefined {
        return (this.block?.data as any)?.backgroundColour ?? (this.block?.data as any)?.colour;
    }

    private getBannerSizeSelection(): string | undefined {
        return (this.block?.data as any)?.bannerSize;
    }

    private getBannerTextAlignmentSelection(): string | undefined {
        return (this.block?.data as any)?.textAlignment;
    }

    private refreshContrast(): void {
        const selection = this.getBackgroundSelection();

        const colors = this.configService.get('colors') as any;
        const bgHex = resolveBrandBackgroundHex(selection, {
            primary: this.primaryColor ?? colors?.primary,
            secondary: this.secondaryColor ?? colors?.secondary,
            tertiary: this.block?.data.tertiary ?? 'var(--color-neutral-offset-white);',
        });

        if (!bgHex) {
            // Keep the existing default (white), which matches current SCSS default.
            return;
        }

        this.foregroundColorHex = pickContrastingTextColor(bgHex) === 'white' ? '#ffffff' : '#000000';
    }

    private getLinkColorSelection(): string | undefined {
        return (this.block?.data as any)?.linkColour ?? (this.block?.data as any)?.linkColor;
    }

    private refreshLinkColor(): void {
        const selection = this.getLinkColorSelection();
        if (!selection) {
            // Clear any explicit override; CSS/SCSS defaults will apply.
            this.linkColorHex = undefined;
            return;
        }

        // Prefer explicit brand CSS variables for link color so theme can adjust globally.
        switch (selection) {
            case 'Link Colour':
            case 'Link Color':
            case 'link':
                this.linkColorHex = 'var(--link-text,)';
                break;
            case 'Secondary Colour':
            case 'Secondary Color':
            case 'secondary':
                this.linkColorHex = 'var(--color-secondary)';
                break;
            case 'Primary Colour':
            case 'Primary Color':
            case 'primary':
            default:
                this.linkColorHex = 'var(--color-primary)';
                break;
        }
    }

    override ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }
}
